import { useEffect } from 'react'
import { seoConfig } from '../data/seo'

type PageMetaProps = {
  title: string
  description: string
  path?: string
  image?: string
  imageAlt?: string
  type?: 'website' | 'article'
  publishedAt?: string
  modifiedAt?: string
  noIndex?: boolean
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>
}

const upsertMeta = (selector: string, attribute: 'name' | 'property', value: string, content: string) => {
  let meta = document.querySelector<HTMLMetaElement>(selector)
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute(attribute, value)
    document.head.appendChild(meta)
  }
  meta.content = content
}

const removeMeta = (selector: string) => document.querySelector(selector)?.remove()

export function PageMeta({
  title,
  description,
  path,
  image = seoConfig.defaultImage,
  imageAlt = seoConfig.defaultImageAlt,
  type = 'website',
  publishedAt,
  modifiedAt,
  noIndex = false,
  structuredData,
}: PageMetaProps) {
  useEffect(() => {
    const fullTitle = `${title} | ${seoConfig.siteName}`
    const routePath = path ?? window.location.pathname
    const canonicalUrl = new URL(routePath, seoConfig.siteUrl).toString()
    const imageUrl = new URL(image, seoConfig.siteUrl).toString()

    document.title = fullTitle
    upsertMeta('meta[name="description"]', 'name', 'description', description)
    upsertMeta('meta[name="robots"]', 'name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large')
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', type)
    upsertMeta('meta[property="og:site_name"]', 'property', 'og:site_name', seoConfig.siteName)
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle)
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description)
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl)
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', imageUrl)
    upsertMeta('meta[property="og:image:alt"]', 'property', 'og:image:alt', imageAlt)
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image')
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle)
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description)
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', imageUrl)
    if (publishedAt) upsertMeta('meta[property="article:published_time"]', 'property', 'article:published_time', publishedAt)
    else removeMeta('meta[property="article:published_time"]')
    if (modifiedAt) upsertMeta('meta[property="article:modified_time"]', 'property', 'article:modified_time', modifiedAt)
    else removeMeta('meta[property="article:modified_time"]')

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = canonicalUrl

    const pageStructuredData = structuredData ?? (routePath === '/'
      ? [
          { '@context': 'https://schema.org', '@type': 'WebSite', name: seoConfig.siteName, url: seoConfig.siteUrl, description },
          { '@context': 'https://schema.org', '@type': 'Person', name: 'Dr. Fathima Finu M. P.', jobTitle: 'General Doctor', url: `${seoConfig.siteUrl}/about`, image: `${seoConfig.siteUrl}/images/dr-fathima-finu.jpg`, sameAs: ['https://www.linkedin.com/in/fathima-finu-3330123b8'] },
        ]
      : {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: title,
          description,
          url: canonicalUrl,
          isPartOf: { '@type': 'WebSite', name: seoConfig.siteName, url: seoConfig.siteUrl },
        })

    document.querySelector('#page-structured-data')?.remove()
    const script = document.createElement('script')
    script.id = 'page-structured-data'
    script.type = 'application/ld+json'
    script.text = JSON.stringify(pageStructuredData).replaceAll('<', '\\u003c')
    document.head.appendChild(script)
  }, [description, image, imageAlt, modifiedAt, noIndex, path, publishedAt, structuredData, title, type])
  return null
}
